import { Extension, type NodeViewRenderer } from "@tiptap/core";
import type { Node as ProseNode } from "@tiptap/pm/model";
import { Plugin } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import type { Pair } from "../chapter_markdown/table_model";
import type { EditorContext } from "./context";
import { element } from "./dom";

export interface Caption {
	title: string;
	number: string | null;
}

const classOf = (pairs: Pair[]) => pairs.find(([name]) => name === "class")?.[1] ?? "";

// A book table drawn as the reader draws it: its own classes on the table, a caption whose title and number are two
// fields, and the rows as the content. The reader's sections do not exist here (every row sits in one tbody), so a
// row carries its section as `data-section`.
export function tableView(ctx: EditorContext): NodeViewRenderer {
	return ({ node: first, editor, getPos }) => {
		let node = first;
		const dom = element("div", "overflow-x-auto");
		const table = element("table", "");
		const body = element("tbody", "");
		table.append(body);
		dom.append(table);
		let caption: { el: HTMLElement; title: HTMLInputElement; number: HTMLInputElement } | undefined;

		const write = () => {
			const pos = getPos();
			if (pos === undefined || !caption) return;
			const number = caption.number.value;
			const next: Caption = { title: caption.title.value, number: number === "" ? null : number };
			editor.view.dispatch(editor.state.tr.setNodeMarkup(pos, undefined, { ...node.attrs, caption: next }));
		};
		const field = (className: string, label: string, placeholder: string) => {
			const input = element("input", className);
			input.type = "text";
			input.placeholder = placeholder;
			input.setAttribute("aria-label", label);
			input.addEventListener("input", write);
			return input;
		};
		const paint = () => {
			table.className = `odo-chapter-table ${classOf(node.attrs.html)}`.trim();
			const shown = node.attrs.caption as Caption | null;
			if (!shown) {
				caption?.el.remove();
				caption = undefined;
				return;
			}
			if (!caption) {
				const el = element("caption", "odo-chapter-caption");
				el.contentEditable = "false";
				const title = field("odo-chapter-caption__title", ctx.t("table_caption_title"), ctx.t("table_caption_title"));
				const number = field("odo-chapter-caption__number", ctx.t("table_caption_number"), ctx.t("table_caption_number_hint"));
				const span = element("span", "");
				span.append(number);
				el.append(title, span);
				table.prepend(el);
				caption = { el, title, number };
			}
			// Typing is the one thing that changes a field before the node does; the node never overwrites it with what it holds.
			if (caption.title.value !== shown.title) caption.title.value = shown.title;
			if (caption.number.value !== (shown.number ?? "")) caption.number.value = shown.number ?? "";
		};
		paint();
		return {
			dom,
			contentDOM: body,
			update: (next) => {
				if (next.type !== node.type) return false;
				node = next;
				paint();
				return true;
			},
			stopEvent: (event) => caption?.el.contains(event.target as Node) ?? false,
			ignoreMutation: (mutation) => !body.contains(mutation.target),
		};
	};
}

// The reader stripes the rows of each tbody from its first; the editor's single tbody holds them all, so the
// odd rows of each run of body rows are marked.
function stripes(doc: ProseNode): Decoration[] {
	const marks: Decoration[] = [];
	doc.descendants((table, tablePos) => {
		if (table.type.spec.tableRole !== "table") return true;
		let run = 0;
		let section = "";
		table.forEach((row, offset) => {
			if (row.attrs.section !== section) run = 0;
			section = row.attrs.section;
			run += 1;
			if (section === "tbody" && run % 2 === 1)
				marks.push(Decoration.node(tablePos + 1 + offset, tablePos + 1 + offset + row.nodeSize, { class: "odo-chapter-odd" }));
		});
		return false;
	});
	return marks;
}

export const TableStripes = Extension.create({
	name: "tableStripes",
	addProseMirrorPlugins: () => [new Plugin({ props: { decorations: (state) => DecorationSet.create(state.doc, stripes(state.doc)) } })],
});
